import React, { useState, useMemo, useEffect } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { percentFigure } from '../../utils/percentFigure';
import { getIndentClass } from '../../utils/styleUtils';

import RevenueMilestoneData from './RevenueMilestoneData';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function ContractRow(props) {
    const [showContractChildren, setShowContractChildren] = useState(false);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'committed' || t.batch === 'projects committed') {
            setShowContractChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Calculate indentation levels
    const indentLevel = props.indentLevel;
    const childIndentLevel = indentLevel + 1;

    const toggleContractShow = () => {
        setShowContractChildren(!showContractChildren);
    };

    const { contract, maxRevenueMilestonesCount } = props;

    // Memoize revenue milestones calculation
    const revenueMilestones = useMemo(() => {
        const result = [];
        if (contract.currentMilestones) {
            for (let i = 0; i < maxRevenueMilestonesCount; i++) {
                let milestoneAmount = 0;
                let milestonePercentage = 0;
                const currentMilestone = contract.currentMilestones[i];

                if (currentMilestone && contract.milestones && contract.milestones.length > 0) {
                    // Find milestone that matches this period
                    const periodDate = new Date(currentMilestone.date);
                    const matchingMilestone = contract.milestones.find((milestone) => {
                        const milestoneDate = new Date(milestone.date);
                        return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
                    });

                    milestoneAmount = currentMilestone.amount || 0;
                    milestonePercentage = matchingMilestone ? matchingMilestone.percentage || 0 : 0;
                }

                result.push({
                    amount: milestoneAmount,
                    date: currentMilestone ? currentMilestone.date : '',
                    percentage: milestonePercentage,
                });
            }
        }
        return result;
    }, [contract.currentMilestones, contract.milestones, maxRevenueMilestonesCount]);

    // Memoize milestone total calculation
    const milestonesTotalValue = useMemo(() => {
        return contract.milestones ? contract.milestones.reduce((sum, milestone) => sum + (milestone.amount || 0), 0) : 0;
    }, [contract.milestones]);

    // Memoize main contract milestone elements
    const contractMilestoneElements = useMemo(() => {
        if (!contract.currentMilestones) return null;

        return [...Array(maxRevenueMilestonesCount)].map((e, i) => (
            <React.Fragment key={`contract_${contract.contract_reference}_milestones_${i}`}>
                <RevenueMilestoneData
                    key={`${props.keyPrefix || ''}contract_${contract.contract_reference}_milestone_${i}`}
                    milestones={contract.currentMilestones}
                    milestoneId={i}
                    keyPrefix={props.keyPrefix || ''}
                    amountOnly={true}
                />
            </React.Fragment>
        ));
    }, [contract.currentMilestones, contract.contract_reference, maxRevenueMilestonesCount, props.keyPrefix]);

    // Memoize child milestone elements
    const childMilestoneElements = useMemo(() => {
        return [...Array(maxRevenueMilestonesCount)].map((e, i) => (
            <React.Fragment key={`revenue_milestone_${i}`}>
                <RevenueMilestoneData
                    key={`${props.keyPrefix || ''}revenue_milestone_${i}`}
                    milestones={revenueMilestones}
                    milestoneId={i}
                    keyPrefix={props.keyPrefix || ''}
                    amountOnly={false}
                    isCommitted={true}
                />
            </React.Fragment>
        ));
    }, [revenueMilestones, maxRevenueMilestonesCount, props.keyPrefix]);

    const contractTotals = useMemo(() => {
        return calculateRowTotals(contract.currentMilestones);
    }, [contract.currentMilestones]);

    const contractChildTotals = useMemo(() => {
        return calculateRowTotals(contract.milestones);
    }, [contract.milestones]);

    return (
        <>
            <tr className="!tw-bg-gray-100">
                <td>
                    <div className={`tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer ${getIndentClass(indentLevel)}`} onClick={toggleContractShow}>
                        <div className="tw-font-medium tw-text-gray-600">{contract.contract_reference}</div>
                        <div>{showContractChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                    </div>
                </td>
                <td className="tw-p-4"></td>
                <td className="tw-p-4 tw-text-right tw-text-gray-600 tw-border-r-2">
                    {formatFigure(contract.value, formatToFixedMoneyWithLocale, { precision: 2 })}
                </td>
                {contractMilestoneElements}
                <td className="tw-p-4 tw-text-right tw-border-r-2"></td>
                <td className="tw-p-4 tw-text-right tw-border-r-2">
                    {formatFigure(contractTotals.totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}
                </td>
                <td className="tw-p-4 tw-text-right tw-border-r-2"></td>
            </tr>
            {showContractChildren && (
                <>
                    {/* Revenue milestones - single type, show directly with percentage */}
                    <tr className="!tw-bg-gray-25">
                        <td className={getIndentClass(childIndentLevel)}>
                            <div className="tw-font-medium tw-text-gray-500"></div>
                        </td>
                        <td className="tw-p-4"></td>
                        <td className="tw-p-4 tw-text-right tw-text-gray-500 tw-border-r-2">
                            {formatFigure(milestonesTotalValue, formatToFixedMoneyWithLocale, { precision: 2 })}
                        </td>
                        {childMilestoneElements}
                        <td className="tw-p-4 tw-text-right">
                            {percentFigure(contractChildTotals.totalPercentage > 100 ? 100 : contractChildTotals.totalPercentage)}
                        </td>
                        <td className="tw-p-4 tw-text-right">{formatFigure(contractChildTotals.totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}</td>
                        <td className="tw-p-4 tw-border-r-2"></td>
                    </tr>
                </>
            )}
        </>
    );
}
